// ============================================================
// Stocora — brand logo component
// ============================================================
// Update #9 wordmark (spec §1, 2026-07-17), replacing the interim
// disc-with-ring "O" mark:
//
//   [S-badge] St(o)cora
//
//   - Squircle icon badge: teal background, white bold "S",
//     border-radius ~7px at the 22px reference (scales with size).
//   - Wordmark: "Stocora" in DM Sans weight 600 (spec value —
//     down from the interim font-extrabold). The lowercase "o"
//     is teal and carries the idle beat animation (`.o-beat` in
//     index.css): scale 1 → 1.14 → 1 over 2.8s ease-in-out,
//     color teal → teal-bright → teal at the same keyframes.
//     This is the ONE piece of ambient motion in the whole app.
//     Per the 2026-07-14 audit it was previously spec'd but never
//     built — no oBeat keyframe existed before this pass.
//   - prefers-reduced-motion disables the beat (index.css).
//
// Native text rendering is kept (not an SVG lockup) for the same
// reason as the previous mark: better letterforms at 16-48px.
// See DECISIONS.md Update #9 entry.
//
// Size prop controls the wordmark cap height in pixels; the badge
// scales proportionally.
// ============================================================

interface StocoraLogoProps {
  /** Wordmark height in pixels. Badge scales proportionally. */
  size?: number;
  /** Optional className passthrough for the outer wrapper. */
  className?: string;
}

export default function StocoraLogo({
  size = 36,
  className = '',
}: StocoraLogoProps) {
  // Badge sized slightly above cap-height so it reads as a peer
  // element, not an inline glyph. Radius scales from the ~7px @
  // 22px spec reference.
  const badgePx = Math.round(size * 1.15);
  const badgeRadius = Math.max(4, Math.round((badgePx * 7) / 22));

  return (
    <span
      className={`inline-flex items-center gap-[0.28em] font-semibold text-slate-900 leading-none select-none ${className}`}
      style={{
        fontSize: `${size}px`,
        letterSpacing: '0.01em',
      }}
      role="img"
      aria-label="Stocora"
    >
      <span
        aria-hidden="true"
        className="inline-flex items-center justify-center bg-action text-white font-bold shrink-0"
        style={{
          width: `${badgePx}px`,
          height: `${badgePx}px`,
          borderRadius: `${badgeRadius}px`,
          fontSize: `${Math.round(badgePx * 0.62)}px`,
        }}
      >
        S
      </span>
      <span aria-hidden="true">
        St
        <span className="o-beat text-action">o</span>
        cora
      </span>
    </span>
  );
}
